import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  LinearProgress,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const WARNING_SECONDS = 120; // 2 minutes

const SessionWarningDialog = ({ open, countdown, onStayLoggedIn, onLogout }) => {
  const progress = Math.max(0, (countdown / WARNING_SECONDS) * 100);

  // Colour shifts from teal → amber → red as time runs out
  const getBarColor = () => {
    if (countdown > 60) return '#008080';
    if (countdown > 30) return '#f59e0b';
    return '#ef4444';
  };

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
        },
      }}
    >
      {/* Coloured progress bar at top */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          backgroundColor: '#e2e8f0',
          '& .MuiLinearProgress-bar': {
            backgroundColor: getBarColor(),
            transition: 'background-color 0.5s ease',
          },
        }}
      />

      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        {/* Icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: countdown <= 30 ? '#fef2f2' : '#f0fdfa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2.5,
            border: `2px solid ${countdown <= 30 ? '#fca5a5' : '#99f6e4'}`,
          }}
        >
          <AccessTimeIcon
            sx={{
              fontSize: 38,
              color: countdown <= 30 ? '#ef4444' : '#008080',
              animation: countdown <= 30 ? 'pulse 1s infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.15)', opacity: 0.7 },
              },
            }}
          />
        </Box>

        {/* Title */}
        <Typography variant="h6" fontWeight={700} color="#1e293b" mb={0.5}>
          Session Expiring Soon
        </Typography>

        {/* Subtitle */}
        <Typography variant="body2" color="text.secondary" mb={3}>
          You've been inactive for a while. For patient data security, your session will
          automatically expire in:
        </Typography>

        {/* Countdown ring */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 90,
            height: 90,
            borderRadius: '50%',
            border: `5px solid ${getBarColor()}`,
            mb: 3,
            transition: 'border-color 0.5s ease',
            boxShadow: `0 0 0 4px ${getBarColor()}22`,
          }}
        >
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: getBarColor(), transition: 'color 0.5s ease', fontVariantNumeric: 'tabular-nums' }}
          >
            {formatCountdown(countdown)}
          </Typography>
        </Box>

        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
          <Button
            variant="contained"
            fullWidth
            onClick={onStayLoggedIn}
            sx={{
              bgcolor: '#008080',
              '&:hover': { bgcolor: '#006666' },
              borderRadius: 2,
              fontWeight: 700,
              py: 1.4,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: '0 4px 14px rgba(0,128,128,0.35)',
            }}
          >
            ✓ Stay Logged In
          </Button>

          <Button
            variant="outlined"
            fullWidth
            onClick={onLogout}
            startIcon={<LockOutlinedIcon />}
            sx={{
              borderColor: '#cbd5e1',
              color: '#64748b',
              borderRadius: 2,
              textTransform: 'none',
              py: 1.2,
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
            }}
          >
            Logout Now
          </Button>
        </Box>

        {/* Footer note */}
        <Typography variant="caption" color="text.disabled" sx={{ mt: 2.5, display: 'block' }}>
          🔒 Patient data is protected. Unsaved drafts will be preserved.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default SessionWarningDialog;
